'use client';

import type { UrlObject } from 'node:url';
import type { Route } from 'next';
import Link from 'next/link';
import type { VariantType } from '../../../types';
import styles from './LinkButton.module.scss';
import raffleStyles from './LinkButton.raffle.module.scss';

export type LinkButtonProps<T extends string = string> = {
  children: React.ReactNode;
  href: Route<T> | UrlObject;
  size?: 'xSmall' | 'default' | 'small' | 'large' | 'xLarge' | 'fullWidth';
  /** 参照: src/styles/partials/_variables.scss */
  color?: string;
  bgColor?: string;
  outlined?: boolean;
  variant?: VariantType;
  dataTestId?: string;
  target?: '_blank' | '_self';
};

function LinkButton<T extends string>({
  children,
  href,
  size = 'default',
  color = 'black',
  bgColor = 'white',
  outlined = false,
  variant = 'default',
  dataTestId = '',
  target = '_self',
}: LinkButtonProps<T>) {
  // variantに応じてスタイルを決定
  // raffleだけ異なるので分ける
  const currentStyles = variant === 'raffle' ? raffleStyles : styles;

  return (
    <div
      className={`${currentStyles.linkButton} ${currentStyles[size]}`}
      style={generateStyle({ color, bgColor, outlined })}
    >
      <Link href={href} prefetch={false} className={currentStyles.link} target={target} data-testid={dataTestId}>
        {children}
      </Link>
    </div>
  );
}

const generateStyle = ({
  color,
  bgColor,
  outlined,
}: {
  color?: string;
  bgColor?: string;
  outlined?: boolean;
}): React.CSSProperties => {
  if (outlined) {
    return {
      color: `var(--color-${color})`,
      border: `2px solid var(--color-${color})`,
      background: `var(--color-${bgColor})`,
    };
  }

  // 色に応じてグラデーション
  switch (color) {
    case 'primary':
      return {
        border: 'none',
        background: 'linear-gradient(to top, var(--color-gradationprimary), var(--color-primary))',
      };
    case 'blue-gradient':
      return {
        border: 'none',
        background: 'linear-gradient(to top, #1190e0, #33b0ff)',
      };
    default:
      return {
        color: 'var(--color-white)',
        border: `var(--color-${color})`,
        background: `var(--color-${color})`,
      };
  }
};

export default LinkButton;
